import React from "react";
import { logger } from "../../utils/logger";
import { pushError } from "../../monitoring/faro";
import Typography from "../Typography";
import Button from "../Button/Button";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error("Error caught by boundary:", error, errorInfo);

    // Report to Grafana Faro with the React component stack as context
    pushError(error, {
      source: "ErrorBoundary",
      componentStack: errorInfo?.componentStack?.slice(0, 500) ?? "",
    });

    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <Typography
              variant="h3"
              component="h2"
              className="text-2xl font-bold text-gray-900 mb-2"
            >
              Something went wrong
            </Typography>
            <Typography className="text-gray-600 mb-6" component="p">
              We're sorry, but something unexpected happened. Please try reloading the page.
            </Typography>
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error Details
                </summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 p-3 rounded overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            <Button
              onClick={this.handleReload}
              className="w-full bg-[var(--button-primary)] text-white px-4 py-2 rounded-md hover:bg-[var(--button-primary-hover)] transition-colors"
            >
              Reload Page
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
